import { inject, Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { MODAL_TOKEN, ModalProvider } from '../tokens';
import { PresentModalComponent } from '../modals';

export interface AvatarResult {
  base64: string;
  dataUrl: string;
}

@Injectable({ providedIn: 'root' })
export class CameraService {
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);

  async pickAvatar(): Promise<AvatarResult | null> {
    try {
      const photo: Photo = await Camera.getPhoto({
        quality: 90,
        width: 600,
        height: 600,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt,
        promptLabelHeader: 'Wybierz zdjęcie',
        promptLabelPhoto: 'Z galerii',
        promptLabelPicture: 'Zrób zdjęcie',
        promptLabelCancel: 'Anuluj'
      });

      if (!photo.base64String) {
        return null;
      }

      const estimatedSize: number = photo.base64String.length * 0.75;
      if (estimatedSize >= 2000000) {
        this.showError('Zdjęcie jest zbyt duże. Maksymalny rozmiar to 2MB.');
        return null;
      }

      const format: string = photo.format || 'jpeg';
      const dataUrl: string = `data:image/${format};base64,${photo.base64String}`;

      return {
        base64: photo.base64String,
        dataUrl
      };
    } catch (error: unknown) {
      const errorMessage: string = error instanceof Error ? error.message : String(error);

      if (this.isUserCancellation(errorMessage)) {
        return null;
      }

      this.showError('Nie udało się pobrać zdjęcia. Spróbuj ponownie.');
      return null;
    }
  }

  private isUserCancellation(message: string): boolean {
    const cancellationMessages: string[] = [
      'user cancelled',
      'user canceled',
      'cancelled',
      'canceled',
      'no image picked',
      'user denied access'
    ];

    const lowerMessage: string = message.toLowerCase();
    return cancellationMessages.some((msg: string) => lowerMessage.includes(msg));
  }

  private showError(message: string): void {
    this.modalProvider.showModal$({
      component: PresentModalComponent,
      componentProps: {
        header: 'Błąd',
        message,
        btnTxt: 'Zamknij'
      },
      cssClass: 'present-modal'
    });
  }
}
